import moment from "moment";
import { AuditDetailItem } from "models";
import type { MovedToBackstockItem } from "models/audit/AuditDetailModel";

export type AuditDashboardScratcherTab = "all" | "mismatched" | "moved_to_backstock";

export const hasScratcherMismatch = (
  quantityVariance: number | null | undefined,
  revenueVariance: number | null | undefined
) => Number(quantityVariance ?? 0) !== 0 || Number(revenueVariance ?? 0) !== 0;

export interface AuditDashboardGroupedScratchRow {
  item: AuditDetailItem;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  groupSize: number;
  rowColor?: string;
}

export interface AuditDashboardScratchTotals {
  qtySold: number;
  soldAmount: number;
  scratcherCount: number;
  scratcherSales: number;
  quantityVariance: number;
  revenueVariance: number;
}

export const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));

export const getEligibilityKey = (
  gameId: string | number | null | undefined,
  packId: string | number | null | undefined
) => {
  const gameDigits = String(gameId ?? "").replace(/\D/g, "");
  const packDigits = String(packId ?? "").replace(/\D/g, "");
  if (!gameDigits || !packDigits) return "";
  return `${Number(gameDigits)}-${Number(packDigits)}`;
};

export const formatBackOnSaleTimestamp = (value: string | null | undefined) => {
  if (!value) return "";
  return moment.parseZone(value).format("MM/DD/YYYY [@] h:mm A");
};

export const getBackOnSaleDestination = (subgroup: string | null | undefined, hasReturnedToCounterEvent = false) => {
  if (subgroup === "Vending") return "Vending";
  if (subgroup === "Counter") return "Counter";
  if (hasReturnedToCounterEvent) return "Counter";
  return "";
};

export const getBackstockLocationLabel = (subgroup: string | null | undefined) => {
  if (subgroup === "Backstock Cashier") return "Cashier";
  if (subgroup === "Backstock Locker") return "Locker";
  return "Backstock";
};

export const formatTicketSegment = (ticketNumber: number | null | undefined) => {
  if (ticketNumber == null || ticketNumber === -1) return "END";
  return (Number(ticketNumber) % 1000).toString().padStart(3, "0");
};

export const getTicketDisplay = (displayValue: string | undefined, ticketNumber: number | null | undefined) =>
  displayValue ?? formatTicketSegment(ticketNumber);

export const getMovedRange = (item: MovedToBackstockItem | any) => {
  const explicitRange = item?.moved_to_backstock_range ?? item?.movedToBackstockRange ?? item?.backstockRange ?? item?.range;
  if (explicitRange) return String(explicitRange);

  const ticketNumberAtMove = Number(item?.ticket_number_at_move ?? item?.ticketNumberAtMove ?? 0);
  const ticketsRemaining = Number(item?.tickets_remaining ?? item?.ticketsRemaining ?? 0);
  if (ticketNumberAtMove > 0) {
    const start = Number(ticketNumberAtMove % 1000);
    const endRaw = ticketsRemaining === 0 ? ticketNumberAtMove : ticketNumberAtMove + ticketsRemaining;
    const end = Number(endRaw % 1000);
    return `${start.toString().padStart(3, "0")}-${end.toString().padStart(3, "0")}`;
  }

  return "-";
};

export const getMovedQty = (item: MovedToBackstockItem | any) =>
  Number(
    item?.moved_to_backstock_count ??
      item?.movedToBackstockCount ??
      item?.backstockMovedCount ??
      item?.tickets_remaining ??
      item?.ticketsRemaining ??
      item?.qty_moved_to_backstock ??
      item?.qtyMovedToBackstock ??
      0
  );

export const getMovedValue = (item: MovedToBackstockItem | any) => {
  const explicitValue = Number(
    item?.moved_to_backstock_value ?? item?.movedToBackstockValue ?? item?.backstockMovedValue ?? NaN
  );
  if (!Number.isNaN(explicitValue)) return explicitValue;
  return getMovedQty(item) * Number(item?.costPerTicket ?? item?.game_cost_per_ticket ?? 0);
};

export const getVarianceRowColor = (item: AuditDetailItem, hasPosType: boolean) => {
  if (!hasPosType) return undefined;
  if (item.quantityVariance < 0 || item.revenueVariance < 0) return "rgba(255, 241, 241, 0.58)";
  if (item.quantityVariance > 0 || item.revenueVariance > 0) return "rgba(232, 247, 253, 0.54)";
  return undefined;
};

export const groupScratchRows = (items: AuditDetailItem[], hasPosType: boolean): AuditDashboardGroupedScratchRow[] => {
  const groups = new Map<string, AuditDetailItem[]>();
  items.forEach((item) => {
    const key = item.gameId || item.id;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  });

  const rows: AuditDashboardGroupedScratchRow[] = [];
  groups.forEach((groupItems) => {
    const rowColor = getVarianceRowColor(groupItems[0], hasPosType);
    groupItems.forEach((item, index) => {
      rows.push({
        item,
        isFirstInGroup: index === 0,
        isLastInGroup: index === groupItems.length - 1,
        groupSize: groupItems.length,
        rowColor,
      });
    });
  });

  return rows;
};

export const calculateDefaultTotals = (items: AuditDetailItem[]): AuditDashboardScratchTotals => {
  const uniqueGameIds = new Set<string>();

  return items.reduce<AuditDashboardScratchTotals>(
    (acc, item) => {
      const isNewGame = !uniqueGameIds.has(item.gameId);
      if (isNewGame) uniqueGameIds.add(item.gameId);

      return {
        qtySold: acc.qtySold + item.qtySold,
        soldAmount: acc.soldAmount + item.inventorySoldAmount,
        scratcherCount: acc.scratcherCount + (isNewGame ? item.posQty : 0),
        scratcherSales: acc.scratcherSales + (isNewGame ? item.posSales : 0),
        quantityVariance: acc.quantityVariance + (isNewGame ? item.quantityVariance : 0),
        revenueVariance: acc.revenueVariance + (isNewGame ? item.revenueVariance : 0),
      };
    },
    {
      qtySold: 0,
      soldAmount: 0,
      scratcherCount: 0,
      scratcherSales: 0,
      quantityVariance: 0,
      revenueVariance: 0,
    }
  );
};

export const calculateMovedTotals = (items: MovedToBackstockItem[]): AuditDashboardScratchTotals =>
  items.reduce<AuditDashboardScratchTotals>(
    (acc, item) => ({
      qtySold: acc.qtySold + getMovedQty(item),
      soldAmount: acc.soldAmount + getMovedValue(item),
      scratcherCount: 0,
      scratcherSales: 0,
      quantityVariance: 0,
      revenueVariance: 0,
    }),
    {
      qtySold: 0,
      soldAmount: 0,
      scratcherCount: 0,
      scratcherSales: 0,
      quantityVariance: 0,
      revenueVariance: 0,
    }
  );

export const countGames = (items: AuditDetailItem[]) => new Set(items.map((item) => item.gameId || item.id)).size;

export const countMismatchedGames = (items: AuditDetailItem[]) => {
  const mismatchedGames = new Set<string>();
  items.forEach((item) => {
    if (item.hasVariance) {
      mismatchedGames.add(item.gameId || item.id);
    }
  });
  return mismatchedGames.size;
};

export const countPacks = (items: AuditDetailItem[]) => items.filter((item) => Boolean(item.packId)).length;

export const buildManualSoldOutEligibilityRequest = (items: MovedToBackstockItem[]) => {
  const seenKeys = new Set<string>();
  const packs: Array<{ game_id: string; pack_id: string }> = [];

  items.forEach((item) => {
    const gameDigits = String(item.gameId ?? "").replace(/\D/g, "");
    const packDigits = String(item.packId ?? "").replace(/\D/g, "");
    if (!gameDigits || !packDigits) return;

    const key = `${Number(gameDigits)}-${Number(packDigits)}`;
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    packs.push({ game_id: gameDigits, pack_id: packDigits });
  });

  return packs;
};
